import {
  ASSESSMENT_STATUSES,
  GRADE_TONE,
  INDUSTRIES,
  SCORE_BANDS,
  type EvidenceGrade,
  type ScoreBrief,
} from '@cyberscore/contracts';
import type { FilterDef } from '../../ui/DataView';
import { Meter } from '../../ui/DataView';
import { STATUS_LABEL, STATUS_TONE } from '../assessments/labels';
import { bandLabel, bandTone } from '../portfolio/format';

/** What lists of clients and assessments share (task 60): filters, and how a score and progress read. */

export const STATUS_FILTER: FilterDef = { key: 'status', label: 'Statuses', options: ASSESSMENT_STATUSES.map((s) => [s, STATUS_LABEL[s]] as const) }; // prettier-ignore
export const BAND_FILTER: FilterDef = { key: 'band', label: 'Bands', options: SCORE_BANDS.map((b) => [b.key, `${bandLabel(b.key)} (${b.min}–${b.max})`] as const) }; // prettier-ignore
export const INDUSTRY_FILTER: FilterDef = {
  key: 'industry',
  label: 'Industries',
  options: Object.entries(INDUSTRIES),
};

export const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });

export function StatusPill({ status }: { status: keyof typeof STATUS_LABEL }) {
  return <span className={`pill ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;
}

/** The evidence grade beside a score (task 63); its reasons in the tooltip and to screen readers. */
export function GradeBadge({ g }: { g: EvidenceGrade }) {
  return (
    <span
      className={`grade ${GRADE_TONE[g.grade]}`}
      title={`Evidence grade ${g.label}`}
      aria-label={`Evidence grade ${g.label}`}
    >
      {g.grade}
    </span>
  );
}

export function ScoreCell({ s }: { s: ScoreBrief | null }) {
  if (!s) return <span className="muted">Not scored yet</span>;
  return (
    <span className="score-cell">
      <b>{s.score}</b>
      <span className={`pill ${bandTone(s.band)}`}>{bandLabel(s.band)}</span>
      <GradeBadge g={s.grade} />
      {s.provisional && (
        <span className="badge" title="Required questions are still open">
          Provisional
        </span>
      )}
    </span>
  );
}

/** Protections answered of those that apply. */
export function ProgressCell({ s }: { s: ScoreBrief | null }) {
  if (!s || !s.applicable) return <span className="muted">–</span>;
  return <Meter value={s.answered / s.applicable} tone={s.answered === s.applicable ? 'ok' : 'warn'} />;
}

export function ExceptionsCell({ s }: { s: ScoreBrief | null }) {
  if (!s) return <span className="muted">–</span>;
  return s.exceptions ? (
    <span className="pill bad" title={s.cap ? `Capped at ${s.cap}` : undefined}>
      {s.exceptions}
    </span>
  ) : (
    <span className="pill ok">None</span>
  );
}

/** The score, band and progress block of a card. */
export function ScoreBlock({ s }: { s: ScoreBrief | null }) {
  return (
    <>
      <div className="ccard-score">
        {s ? (
          <>
            <b>{s.score}</b>
            <span className={`pill ${bandTone(s.band)}`}>{bandLabel(s.band)}</span>
            <GradeBadge g={s.grade} />
            {s.provisional && <span className="badge">Provisional</span>}
          </>
        ) : (
          <span className="muted">Not scored yet</span>
        )}
      </div>
      {s && <div className="muted small grade-why">Evidence grade {s.grade.label}</div>}
      <div className="progress-line">
        <div className="row-between">
          <span className="muted">Protections answered</span>
          <span className="num">{s?.applicable ? `${s.answered} of ${s.applicable}` : '–'}</span>
        </div>
        <span className="meter thin">
          <span className={`fill ${s && s.answered === s.applicable ? 'ok' : 'warn'}`} style={{ width: `${s?.applicable ? Math.round((s.answered / s.applicable) * 100) : 0}%` }} />
        </span>
      </div>
    </>
  ); // prettier-ignore
}
